import React from 'react';

import {
  FileRequestType,
  LinkRequestType,
  TestimonialText,
} from '~/types/models/testimonial';
import {
  parseStringToRichText,
  BlockElementStyle,
  defaultEmptyRichTextValue,
  parseRichTextToSlateData,
  RichText,
} from '@vimily/bonjoro-ui-components-v2/RichTextEditor';
import { Element } from 'slate';

const FILE_REQUESTS_FIELDS_TOTAL = 5;
const LINK_REQUESTS_FIELDS_TOTAL = 8;
const MAX_FILE_REQUEST = 3;
const MAX_LINK_REQUEST = 4;

enum RequestCustomType {
  File = 'file',
  Link = 'link',
}

const requestCustomTypes = {
  [RequestCustomType.File]: FileRequestType.CustomFile,
  [RequestCustomType.Link]: LinkRequestType.CustomLink,
};

const socialIcons = {
  [LinkRequestType.Facebook]:
    'https://cdn.bonjoro.com/assets/logos/socials/facebook.svg',
  [LinkRequestType.LinkedIn]:
    'https://cdn.bonjoro.com/assets/logos/socials/linked-in.png',
  [LinkRequestType.Twitter]:
    'https://cdn.bonjoro.com/assets/logos/socials/twitter.svg',
  [LinkRequestType.Website]:
    'https://cdn.bonjoro.com/assets/logos/socials/web.svg',
};

const labels = {
  [LinkRequestType.Facebook]: (
    <span className='flex justify-between align-center gap-x-3 items-center'>
      <img
        src={socialIcons[LinkRequestType.Facebook]}
        className='rounded h-5 w-5'
      />
      Facebook link
    </span>
  ),
  [LinkRequestType.LinkedIn]: (
    <span className='flex justify-between align-center gap-x-3 items-center'>
      <img
        src={socialIcons[LinkRequestType.LinkedIn]}
        className='rounded h-5 w-5'
      />
      LinkedIn profile
    </span>
  ),
  [LinkRequestType.Twitter]: (
    <span className='flex justify-between align-center gap-x-3 items-center'>
      <img
        src={socialIcons[LinkRequestType.Twitter]}
        className='rounded h-5 w-5'
      />
      X (Twitter) Link
    </span>
  ),
  [LinkRequestType.Website]: (
    <span className='flex justify-between align-center gap-x-3 items-center'>
      <img
        src={socialIcons[LinkRequestType.Website]}
        className='rounded h-5 w-5'
      />
      Website link
    </span>
  ),
};

const staticFileRequests = {
  [FileRequestType.CompanyLogo]: {
    label: 'Company logo',
    type: FileRequestType.CompanyLogo,
  },
  [FileRequestType.ProfilePhoto]: {
    label: 'Profile photo',
    type: FileRequestType.ProfilePhoto,
  },
};

const staticLinkRequests = {
  [LinkRequestType.Facebook]: {
    label: 'Facebook link',
    type: LinkRequestType.Facebook,
  },
  [LinkRequestType.LinkedIn]: {
    label: 'Linkedin link',
    type: LinkRequestType.LinkedIn,
  },
  [LinkRequestType.Twitter]: {
    label: 'Twitter link',
    type: LinkRequestType.Twitter,
  },
  [LinkRequestType.Website]: {
    label: 'Website link',
    type: LinkRequestType.Website,
  },
};

export {
  FILE_REQUESTS_FIELDS_TOTAL,
  LINK_REQUESTS_FIELDS_TOTAL,
  MAX_FILE_REQUEST,
  MAX_LINK_REQUEST,
  RequestCustomType,
  requestCustomTypes,
  labels,
  socialIcons,
  staticFileRequests,
  staticLinkRequests,
};

export enum PreviewLayout {
  Desktop,
  Mobile,
}

export const defaultTestimonialText: TestimonialText = {
  intro: '',
  instruction: '',
  outro: '',
  intro_rich: parseRichTextToSlateData(
    parseStringToRichText(
      'Hey, thanks for doing this. It will help us get to know you better and understand why you like our product. Just enter your name and email and hit "Let\'s Go".'
    )
  ),
  instruction_rich: parseRichTextToSlateData([
    {
      type: BlockElementStyle.HeadingTwo,
      children: [
        {
          text: 'Before you begin',
          bold: true,
        },
      ],
    },
    ...defaultEmptyRichTextValue,
    {
      type: BlockElementStyle.NumberedList,
      children: [
        {
          type: BlockElementStyle.ListItem,
          children: [
            {
              text: 'Remember to read the questions carefully before recording or typing down your answer.',
            },
          ],
        },
        {
          type: BlockElementStyle.ListItem,
          children: [
            {
              text: 'Please find somewhere nice and bright with good, even lighting and minimal background noise if you are recording your videos directly. You can also review the questions and record your videos outside then upload them from your computer.',
            },
          ],
        },
        {
          type: BlockElementStyle.ListItem,
          children: [
            {
              text: 'Complete your testimonial by granting your consent and clicking Submit at the end of the questionnaire.',
            },
          ],
        },
        {
          type: BlockElementStyle.ListItem,
          children: [
            {
              text: 'Lastly, if you want to continue later, you may resume submitting your testimonial by clicking the unique link we sent on your email.',
            },
          ],
        },
      ] as Element[],
    },
  ] as RichText),
  closing_message_rich: parseRichTextToSlateData([
    {
      type: BlockElementStyle.HeadingTwo,
      children: [
        {
          text: 'All done!',
        },
      ],
    },
    {
      type: BlockElementStyle.HeadingTwo,
      children: [
        {
          text: 'Complete your response by confirming your consent and clicking Submit below.',
        },
      ],
    },
  ]),
  consent_statement_rich: parseRichTextToSlateData(
    parseStringToRichText(
      'I consent to the use of my provided files and testimonial.'
    )
  ),
  outro_rich: parseRichTextToSlateData(
    parseStringToRichText('Thank you for filling these out. You’re awesome!')
  ),
};

export const defaultProjectName = 'Name this request';
